import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';
import 'bpmn-js/dist/assets/diagram-js.css';
import BpmnJS from 'bpmn-js/lib/Modeler';
import Canvas from 'diagram-js/lib/core/Canvas';
import ElementRegistry from 'diagram-js/lib/core/ElementRegistry';
import EventBus from 'diagram-js/lib/core/EventBus';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import CustomPaletteProvider from './CustomPalleteViewer';
import ColorPickerPanel from './ColorPicker';
import { useMediaQuery, useTheme } from '@mui/material';

interface BpmnViewerProps {
  xml: string;
}

export interface BpmnViewerRef {
  zoomIn: () => void;
  zoomOut: () => void;
  zoomFit: () => void;
  setZoom: (level: number) => void;
  setPanX: (x: number) => void;
  saveXML: () => Promise<string | undefined>;
  importXML: (xmlString: string) => Promise<void>;
  getCurrentZoom: () => number;
  getEventBus?: () => EventBus | undefined;
}

const BpmnViewer = forwardRef<BpmnViewerRef, BpmnViewerProps>(({ xml }, ref) => {
  const [showPicker, setShowPicker] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const modelerRef = useRef<BpmnJS | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const centerDiagram = () => {
    if (!modelerRef.current || !containerRef.current) return;

    const canvas = modelerRef.current.get('canvas') as Canvas;
    const container = containerRef.current;

    const definitions = (modelerRef.current.get('elementRegistry') as ElementRegistry).getAll();

    if (definitions.length === 0) return;

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    definitions.forEach((element: any) => {
      if (element.x !== undefined && element.y !== undefined) {
        minX = Math.min(minX, element.x);
        minY = Math.min(minY, element.y);
        maxX = Math.max(maxX, element.x + (element.width || 0));
        maxY = Math.max(maxY, element.y + (element.height || 0));
      }
    });

    const padding = 50;
    const diagramWidth = maxX - minX + padding * 2;
    const diagramHeight = maxY - minY + padding * 2;
    const diagramCenterX = minX + (maxX - minX) / 2;
    const diagramCenterY = minY + (maxY - minY) / 2;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const scaleX = containerWidth / diagramWidth;
    const scaleY = containerHeight / diagramHeight;
    const scale = Math.min(scaleX, scaleY) * 0.85;

    canvas.zoom(scale);

    const viewboxWidth = containerWidth / scale;
    const viewboxHeight = containerHeight / scale;

    canvas.viewbox({
      x: diagramCenterX - viewboxWidth / 2,
      y: diagramCenterY - viewboxHeight / 2,
      width: viewboxWidth,
      height: viewboxHeight,
    });
  };

  useEffect(() => {
    const paletteContainer = document.querySelector('.djs-palette-entries');
    if (paletteContainer) {
      const allEntries = paletteContainer.querySelectorAll('.entry');
      paletteContainer.innerHTML = ''; // remove group wrappers
      allEntries.forEach((entry) => paletteContainer.appendChild(entry));
    }
  }, []);

  // Mobile touch handling for BPMN canvas
  const setupMobileTouchHandling = () => {
    if (!isMobile || !containerRef.current) return;

    const container = containerRef.current;
    let isScrolling = false;
    let startX = 0;
    let startY = 0;
    let scrollLeft = 0;
    let scrollTop = 0;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        isScrolling = true;
        startX = touch.pageX;
        startY = touch.pageY;

        const canvas = modelerRef.current?.get('canvas') as Canvas;
        if (canvas) {
          const viewbox = canvas.viewbox();
          scrollLeft = viewbox.x;
          scrollTop = viewbox.y;
        }
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isScrolling || e.touches.length !== 1) return;

      e.preventDefault();
      const touch = e.touches[0];
      const deltaX = (touch.pageX - startX) * 2; // Multiply for sensitivity
      const deltaY = (touch.pageY - startY) * 2;

      const canvas = modelerRef.current?.get('canvas') as Canvas;
      if (canvas) {
        const viewbox = canvas.viewbox();
        canvas.viewbox({
          x: scrollLeft - deltaX,
          y: scrollTop - deltaY,
          width: viewbox.width,
          height: viewbox.height,
        });
      }
    };

    const handleTouchEnd = () => {
      isScrolling = false;
    };

    // Add event listeners
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  };

  // useEffect(() => {
  //   if (!containerRef.current) return;

  //   const modeler = new BpmnJS({
  //     container: containerRef.current,
  //     additionalModules: [
  //       {
  //         __init__: ['customPaletteProvider'],
  //         customPaletteProvider: ['type', CustomPaletteProvider],
  //       },
  //     ],
  //   });
  //   modelerRef.current = modeler;

  //   (window as any).bpmnViewer = modeler;

  //   modeler.importXML(xml).then(() => {
  //     setTimeout(() => {
  //       centerDiagram();
  //     }, 100);
  //   });

  //   return () => {
  //     modeler.destroy();
  //   };
  // }, [xml]);

  useEffect(() => {
    if (!containerRef.current) return;

    const modeler = new BpmnJS({
      container: containerRef.current,
      additionalModules: [
        {
          __init__: ['customPaletteProvider'],
          customPaletteProvider: ['type', CustomPaletteProvider],
        },
      ],
    });
    modelerRef.current = modeler;

    (window as any).bpmnViewer = modeler;

    modeler.importXML(xml).then(() => {
      setTimeout(() => {
        centerDiagram();

        if (isMobile) {
          setupMobileTouchHandling();
        }
      }, 100);
    });

    const handler = () => setShowPicker(true);
    window.addEventListener('open-color-picker', handler);

    return () => {
      modeler.destroy();
      window.removeEventListener('open-color-picker', handler);
    };
  }, [xml, isMobile]);

  useEffect(() => {
    if (!modelerRef.current) return;

    const directEditing = modelerRef.current.get('directEditing');

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!modelerRef.current) return;

      const selection = modelerRef.current.get('selection') as any;
      const modeling = modelerRef.current.get('modeling') as any;
      const selectedElements = selection.get();

      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedElements.length > 0) {
        if ((directEditing as any).isActive()) {
          (directEditing as any).complete();
        }
        event.preventDefault();
        event.stopPropagation();

        selectedElements.forEach((element: any) => {
          if (element.id !== '__implicitroot' && !element.isRoot) {
            try {
              modeling.removeElements([element]);
            } catch (error) {
              console.warn('Could not delete element:', element.id, error);
            }
          }
        });
      }

      if (event.key === 'Escape') {
        (directEditing as any).cancel();
      }
    };

    if (containerRef.current) {
      containerRef.current.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, []);

  useEffect(() => {
    const handleRecenter = () => {
      centerDiagram();
    };

    window.addEventListener('recenterDiagram', handleRecenter);

    return () => {
      window.removeEventListener('recenterDiagram', handleRecenter);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    zoomIn: () => {
      const canvas = modelerRef.current?.get('canvas') as Canvas;
      if (canvas) {
        const currentZoom = canvas.zoom() || 1;
        const newZoom = Math.min(currentZoom + 0.1, 2);
        canvas.zoom(newZoom);
      }
    },
    zoomOut: () => {
      const canvas = modelerRef.current?.get('canvas') as Canvas;
      if (canvas) {
        const currentZoom = canvas.zoom() || 1;
        const newZoom = Math.max(currentZoom - 0.1, 0.1);
        canvas.zoom(newZoom);
      }
    },
    zoomFit: () => {
      const canvas = modelerRef.current?.get('canvas') as Canvas;
      if (canvas) {
        canvas.zoom('fit-viewport');
      }
    },
    setZoom: (level: number) => {
      const canvas = modelerRef.current?.get('canvas') as Canvas;
      if (canvas) {
        const clampedLevel = Math.max(0.1, Math.min(2, level));
        canvas.zoom(clampedLevel);
      }
    },
    setPanX: (x: number) => {
      const canvas = modelerRef.current?.get('canvas') as Canvas;
      if (canvas) {
        const viewbox = canvas.viewbox();
        canvas.viewbox({
          x,
          y: viewbox.y,
          width: viewbox.width,
          height: viewbox.height,
        });
      }
    },
    getCurrentZoom: () => {
      const canvas = modelerRef.current?.get('canvas') as Canvas;
      return canvas ? canvas.zoom() || 1 : 1;
    },
    saveXML: async () => {
      if (modelerRef.current) {
        try {
          const directEditing = modelerRef.current.get('directEditing');
          if ((directEditing as any).isActive()) {
            (directEditing as any).complete();
          }
          const { xml } = await modelerRef.current.saveXML({ format: true });
          return xml;
        } catch (error) {
          console.error('Error saving XML:', error);
          return undefined;
        }
      }
      return undefined;
    },
    importXML: async (xmlString: string) => {
      if (modelerRef.current) {
        try {
          await modelerRef.current.importXML(xmlString);
          setTimeout(() => {
            centerDiagram();
            if (isMobile) {
              setupMobileTouchHandling();
            }
          }, 100);
        } catch (error) {
          console.error('Error importing XML:', error);
        }
      }
    },
    getEventBus: () => {
      return modelerRef.current?.get('eventBus');
    },
  }));

  function getContrastColor(hex: string): string {
    if (!hex) return '#ffffff';
    const c = hex.replace('#', '');
    const full =
      c.length === 3
        ? c
            .split('')
            .map((ch) => ch + ch)
            .join('')
        : c;
    const r = parseInt(full.substring(0, 2), 16);
    const g = parseInt(full.substring(2, 4), 16);
    const b = parseInt(full.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          outline: 'none',
          overflow: 'hidden',
          position: 'relative',
          touchAction: isMobile ? 'none' : 'auto',
        }}
        tabIndex={0}
      />

      {showPicker && (
        <ColorPickerPanel
          onChangeColor={(color: string) => {
            const bpmnViewer = (window as any).bpmnViewer;
            const selection = bpmnViewer.get('selection').get();
            const modeling = bpmnViewer.get('modeling');
            const canvas = bpmnViewer.get('canvas');

            if (!selection.length) return;

            const labelColor = getContrastColor(color);

            selection.forEach((el: any) => {
              modeling.setColor(el, { fill: color });
              const gfx = canvas.getGraphics(el);
              if (!gfx) return;

              const textNodes = Array.from(
                gfx.querySelectorAll('.djs-label text, text')
              ) as Element[];
              textNodes.forEach((t) => {
                (t as SVGElement).setAttribute('fill', labelColor);
                (t as HTMLElement).style.setProperty('fill', labelColor, 'important');
              });

              const foreignObjects = Array.from(gfx.querySelectorAll('foreignObject')) as Element[];
              foreignObjects.forEach((fo) => {
                const inner = fo.querySelectorAll('*');
                inner.forEach((node) => {
                  (node as HTMLElement).style.color = labelColor;
                });
              });
            });
          }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
});

export default BpmnViewer;
